import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useFormHorarioClienteContext } from './Context';
import { LoadFailure, SlotGrid, SlotSkeleton, WeekStrip } from '../../booking/BookingParts';
import { AgendaClienteService } from '../../cliente/Service';
import { useEstabsContext } from '../../../../contexts/EstabsContext';
import { debounce } from 'lodash';
import { describeRegra, estimateOcorrencias, isPeriodoValido } from '../../../recorrencias/helpers';

const horarioService = new AgendaClienteService();

// Limite de dias consultados, um a um, na busca pelo proximo dia com vaga.
const SEARCH_LIMIT = 14;

type DiaSemana =
    | 'domingo'
    | 'segunda'
    | 'terca'
    | 'quarta'
    | 'quinta'
    | 'sexta'
    | 'sabado';

type OcorrenciaMensal = 'primeira' | 'segunda' | 'terceira' | 'quarta' | 'ultima';

const diasSemanaOptions: Array<{ value: DiaSemana; label: string }> = [
    { value: 'segunda', label: 'segunda-feira' },
    { value: 'terca', label: 'terça-feira' },
    { value: 'quarta', label: 'quarta-feira' },
    { value: 'quinta', label: 'quinta-feira' },
    { value: 'sexta', label: 'sexta-feira' },
    { value: 'sabado', label: 'sábado' },
    { value: 'domingo', label: 'domingo' },
];

const ocorrenciasMensaisOptions: Array<{ value: OcorrenciaMensal; label: string }> = [
    { value: 'primeira', label: 'Primeira' },
    { value: 'segunda', label: 'Segunda' },
    { value: 'terceira', label: 'Terceira' },
    { value: 'quarta', label: 'Quarta' },
    { value: 'ultima', label: 'Última' },
];

const diaSemanaFromDate = (value: Date | moment.Moment): DiaSemana => {
    const date = moment(value).isValid() ? moment(value) : moment();
    const dias: DiaSemana[] = [
        'domingo',
        'segunda',
        'terca',
        'quarta',
        'quinta',
        'sexta',
        'sabado',
    ];

    return dias[date.day()];
};

const ocorrenciaMensalFromDate = (value: Date | moment.Moment): OcorrenciaMensal => {
    const date = moment(value).isValid() ? moment(value) : moment();

    if (date.clone().add(7, 'days').month() !== date.month()) {
        return 'ultima';
    }

    const occurrence = Math.ceil(date.date() / 7);
    if (occurrence <= 1) return 'primeira';
    if (occurrence === 2) return 'segunda';
    if (occurrence === 3) return 'terceira';
    return 'quarta';
};

const getDiaSemanaLabel = (value?: string) =>
    diasSemanaOptions.find((option) => option.value === value)?.label || 'dia da semana';

const getOcorrenciaMensalLabel = (ocorrencia: OcorrenciaMensal, diaSemana?: string) => {
    const ocorrenciaLabel =
        ocorrenciasMensaisOptions.find((option) => option.value === ocorrencia)?.label ||
        'Ocorrência';

    return `${ocorrenciaLabel} ${getDiaSemanaLabel(diaSemana)} do mês`;
};

const frequenciaPresets = [
    { key: 'semanal', label: 'Semanal', tipo: 'semanas', frequencia: 1 },
    { key: 'quinzenal', label: 'Quinzenal', tipo: 'semanas', frequencia: 2 },
    { key: 'mensal', label: 'Mensal', tipo: 'meses', frequencia: 1 },
] as const;

const getDefaultRecorrenciaEndDate = (
    startDate: moment.Moment,
    tipo: 'dias' | 'semanas' | 'meses',
) => {
    if (tipo === 'meses') return startDate.clone().add(12, 'months').toDate();
    if (tipo === 'semanas') return startDate.clone().add(12, 'weeks').toDate();
    return startDate.clone().add(30, 'days').toDate();
};

export const SelectHorario = () => {
    const { estabSession } = useEstabsContext();

    const [loading, setLoading] = useState({
        horarios: false,
    });
    const [horarios, setHorarios] = useState([] as any);
    const [recorrenciaFimTouched, setRecorrenciaFimTouched] = useState(false);
    const [recorrenciaInicioTouched, setRecorrenciaInicioTouched] = useState(false);
    const [frequenciaPersonalizada, setFrequenciaPersonalizada] = useState(false);

    const {
        date,
        data,
        handlePreviousWeek,
        handleNextWeek,
        setDate,
        servico,
        horario,
        setData,
        atendente,
        setHorario,
        loading: loadingForm,
        recorrencia,
        setRecorrencia,
        recurrenceMode,
        duration,
        setDuration,
        doc,
    } = useFormHorarioClienteContext();

    useEffect(() => {
        // Ao voltar para a etapa, ou com horario vindo da edicao ou da faixa da agenda, mantem o escolhido.
        if (!(data instanceof Date && moment(data).isSame(date, 'day'))) {
            setData(date.toDate());
            setHorario(0);
        }
        if (recurrenceMode && !recorrenciaInicioTouched) {
            const selectedDate = moment(date).startOf('day');
            setRecorrencia((prev) => ({
                ...prev,
                inicio: selectedDate.toDate(),
                fim: recorrenciaFimTouched
                    ? prev.fim
                    : getDefaultRecorrenciaEndDate(selectedDate, prev.tipo),
                diaSemana: diaSemanaFromDate(selectedDate),
                ocorrenciaMensal: ocorrenciaMensalFromDate(selectedDate),
                diaMes: selectedDate.date(),
            }));
        }
    }, [date]);

    const requestSeq = useRef(0);
    const autoSearched = useRef(false);
    const [loadError, setLoadError] = useState(false);
    const [searching, setSearching] = useState(false);
    const [noneAhead, setNoneAhead] = useState(false);
    const [retry, setRetry] = useState(0);

    const fetchDay = (day: moment.Moment) =>
        horarioService.getHorarios(estabSession._id, day.toDate(), servico._id, atendente.uid, duration, doc?._id);

    const searchNextDay = async (seq: number) => {
        setSearching(true);
        try {
            const day = moment(date).add(1, 'day');
            for (let tries = 0; tries < SEARCH_LIMIT; tries += 1) {
                const { data: slots } = await fetchDay(day);
                if (seq !== requestSeq.current) return;
                if (Array.isArray(slots) && slots.length) {
                    setDate(day.clone());
                    return;
                }
                day.add(1, 'day');
            }
            setNoneAhead(true);
        } catch {
            if (seq === requestSeq.current) setNoneAhead(true);
        } finally {
            if (seq === requestSeq.current) setSearching(false);
        }
    };

    const loadHorarios = async () => {
        const seq = ++requestSeq.current;
        try {
            setLoading((prev) => ({ ...prev, horarios: true }));
            setLoadError(false);
            setNoneAhead(false);
            if (estabSession?._id && atendente?.uid && servico?._id) {
                const { data: slots } = await fetchDay(date);
                if (seq !== requestSeq.current) return;
                const list = Array.isArray(slots) ? slots : [];
                setHorarios(list);
                // Sem data definida, nao para em um dia fechado: abre no primeiro dia com vaga.
                if (!list.length && !autoSearched.current && !doc?.startAt && !doc?._id) {
                    autoSearched.current = true;
                    void searchNextDay(seq);
                }
            } else {
                setHorarios([]);
            }
        } catch (error) {
            if (seq !== requestSeq.current) return;
            setHorarios([]);
            setLoadError(true);
        } finally {
            if (seq === requestSeq.current) setLoading((prev) => ({ ...prev, horarios: false }));
        }
    };

    const debouncedLoadHorarios: ReturnType<typeof debounce> = useMemo(
        () => debounce(loadHorarios, 500),
        [estabSession?._id, atendente?.uid, servico?._id, date, duration, retry],
    );

    useEffect(() => {
        if (!(estabSession?._id && atendente?.uid && servico?._id)) {
            debouncedLoadHorarios.cancel();
            setHorarios([]);
            setLoading((prev) => ({ ...prev, horarios: false }));
            return;
        }

        setHorarios([]);
        setLoading((prev) => ({ ...prev, horarios: true }));
        debouncedLoadHorarios();

        return () => {
            debouncedLoadHorarios.cancel();
        };
    }, [atendente?.uid, servico?._id, estabSession?._id, data, duration, debouncedLoadHorarios]);

    const buildMonthlyDefaults = (value: Date | moment.Moment) => {
        const selectedDate = moment(value).isValid() ? moment(value) : moment();
        return {
            diaSemana: diaSemanaFromDate(selectedDate),
            ocorrenciaMensal: ocorrenciaMensalFromDate(selectedDate),
            diaMes: selectedDate.date(),
        };
    };

    const handleTipoRecorrenciaChange = (tipo: 'dias' | 'semanas' | 'meses') => {
        const startDate = recorrencia.inicio
            ? moment(recorrencia.inicio)
            : moment(data instanceof Date ? data : new Date(data));
        const monthlyDefaults = buildMonthlyDefaults(startDate);

        setRecorrencia((prev) => ({
            ...prev,
            tipo,
            fim: recorrenciaFimTouched
                ? prev.fim
                : getDefaultRecorrenciaEndDate(startDate, tipo),
            diaSemana:
                tipo === 'semanas' || tipo === 'meses'
                    ? prev.diaSemana || monthlyDefaults.diaSemana
                    : prev.diaSemana,
            modoMensal: tipo === 'meses' ? prev.modoMensal || 'diaSemana' : prev.modoMensal,
            ocorrenciaMensal:
                tipo === 'meses'
                    ? prev.ocorrenciaMensal || monthlyDefaults.ocorrenciaMensal
                    : prev.ocorrenciaMensal,
            diaMes: tipo === 'meses' ? prev.diaMes || monthlyDefaults.diaMes : prev.diaMes,
        }));
    };

    const handlePresetChange = (preset: (typeof frequenciaPresets)[number]) => {
        setFrequenciaPersonalizada(false);
        handleTipoRecorrenciaChange(preset.tipo);
        setRecorrencia((prev) => ({ ...prev, frequencia: preset.frequencia }));
    };

    const presetAtivo = frequenciaPersonalizada
        ? undefined
        : frequenciaPresets.find(
              (preset) =>
                  preset.tipo === recorrencia.tipo && preset.frequencia === Number(recorrencia.frequencia),
          );
    const periodoValido = isPeriodoValido(recorrencia.inicio, recorrencia.fim);
    const ocorrenciasEstimadas = estimateOcorrencias(recorrencia);

    const handleInicioRecorrenciaChange = (value: string) => {
        setRecorrenciaInicioTouched(true);
        const inicio = value ? moment(value).local().toDate() : null;

        setRecorrencia((prev) => {
            if (!inicio || prev.tipo !== 'meses') {
                return {
                    ...prev,
                    inicio,
                    fim:
                        inicio && !recorrenciaFimTouched
                            ? getDefaultRecorrenciaEndDate(moment(inicio), prev.tipo)
                            : prev.fim,
                };
            }

            const monthlyDefaults = buildMonthlyDefaults(inicio);
            return {
                ...prev,
                inicio,
                fim: !recorrenciaFimTouched
                    ? getDefaultRecorrenciaEndDate(moment(inicio), prev.tipo)
                    : prev.fim,
                diaSemana: monthlyDefaults.diaSemana,
                ocorrenciaMensal: monthlyDefaults.ocorrenciaMensal,
                diaMes: monthlyDefaults.diaMes,
            };
        });
    };

    return (
        <div
            id="agenda-step-horario"
            className="w-full"
        >
            {servico?._id && (
                !atendente?.uid ? (
                    <p className="py-4 text-center text-sm text-base-content/70">Selecione um profissional para continuar.</p>
                ) : (
                    <div className="space-y-5">
                            <WeekStrip
                timezone={estabSession?.timezone}
                                date={date}
                                onSelect={(day) => setDate(day)}
                                onPrevious={handlePreviousWeek}
                                onNext={handleNextWeek}
                                disabled={loadingForm}
                            />

                            <label className="flex items-center justify-between gap-3 rounded-2xl bg-base-100 px-4 py-2.5 ring-1 ring-base-content/[0.06]">
                                <span className="text-sm text-base-content">Duração do atendimento</span>
                                <span className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        className="input input-bordered h-9 min-h-9 w-20 rounded-xl text-center text-base"
                                        value={duration}
                                        onChange={(e) => setDuration(parseInt(e.target.value))}
                                        aria-label="Duração total em minutos"
                                    />
                                    <span className="text-xs text-base-content/55">min</span>
                                </span>
                            </label>

                            {loading?.horarios || searching ? (
                                <SlotSkeleton label={searching ? 'Procurando o próximo dia com horário' : 'Carregando horários'} />
                            ) : loadError ? (
                                <LoadFailure message="Não foi possível carregar os horários." onRetry={() => setRetry((value) => value + 1)} />
                            ) : horarios.length === 0 ? (
                                <div className="py-4 text-center">
                                    <p className="font-medium text-base-content">Sem horários em {moment(date).format('dddd, DD/MM')}</p>
                                    <p className="mx-auto mt-1 max-w-xs text-sm text-base-content/60">
                                        {noneAhead ? 'Também não há vagas nos próximos dias. Tente outra semana ou outra duração.' : 'Escolha outro dia ou ajuste a duração.'}
                                    </p>
                                    {!noneAhead && (
                                        <button type="button" className="btn btn-primary mt-4 h-11 min-h-11 rounded-full px-6" onClick={() => void searchNextDay(requestSeq.current)}>
                                            Ver próximo dia com vaga
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <SlotGrid slots={horarios} selected={horario} onSelect={setHorario} disabled={loadingForm} />
                            )}

                            {recurrenceMode && !doc?._id && (
                                <div className="min-w-0 space-y-4">
                                    <div>
                                        <h3 className="text-sm font-semibold text-base-content">Repetição</h3>
                                        <p className="mt-0.5 text-xs text-base-content/55">Escolha com que frequência e até quando o atendimento se repete.</p>
                                    </div>
                                    <div className="flex flex-col items-stretch gap-4 rounded-2xl bg-base-content/5 p-4">
                                        <div className="sf-segmented w-full max-w-lg" role="group" aria-label="Frequência">
                                            {frequenciaPresets.map((preset) => (
                                                <button
                                                    key={preset.key}
                                                    type="button"
                                                    aria-pressed={presetAtivo?.key === preset.key}
                                                    onClick={() => handlePresetChange(preset)}
                                                >
                                                    {preset.label}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                aria-pressed={!presetAtivo}
                                                onClick={() => setFrequenciaPersonalizada(true)}
                                            >
                                                Outra
                                            </button>
                                        </div>

                                        {!presetAtivo && (
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm">A cada</span>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    aria-label="Intervalo da repetição"
                                                    className="input input-sm input-bordered w-20"
                                                    value={recorrencia.frequencia}
                                                    onChange={(e) =>
                                                        setRecorrencia((prev) => ({
                                                            ...prev,
                                                            frequencia: parseInt(e.target.value) || 1,
                                                        }))
                                                    }
                                                />
                                                <select
                                                    aria-label="Unidade da repetição"
                                                    className="select select-sm select-bordered w-32"
                                                    value={recorrencia.tipo}
                                                    onChange={(e) =>
                                                        handleTipoRecorrenciaChange(
                                                            e.target.value as 'dias' | 'semanas' | 'meses',
                                                        )
                                                    }
                                                >
                                                    <option value="dias">dias</option>
                                                    <option value="semanas">semanas</option>
                                                    <option value="meses">meses</option>
                                                </select>
                                            </div>
                                        )}

                                        {recorrencia.tipo === 'semanas' && (
                                            <div className="flex flex-wrap items-center gap-2">
                                                {recorrencia.diaSemana && (
                                                    <span className="text-sm">às</span>
                                                )}
                                                <select
                                                    className="select select-sm select-bordered w-40"
                                                    value={recorrencia.diaSemana}
                                                    onChange={(e) =>
                                                        setRecorrencia((prev) => ({
                                                            ...prev,
                                                            diaSemana: e.target.value,
                                                        }))
                                                    }
                                                >
                                                    <option value="">Dia da Semana</option>
                                                    {diasSemanaOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {recorrencia.tipo === 'meses' && (
                                            <div className="flex flex-col items-start gap-3">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    {recorrencia.diaSemana && (
                                                        <span className="text-sm">às</span>
                                                    )}
                                                    <select
                                                        className="select select-sm select-bordered w-44"
                                                        value={recorrencia.diaSemana}
                                                        onChange={(e) =>
                                                            setRecorrencia((prev) => ({
                                                                ...prev,
                                                                modoMensal: 'diaSemana',
                                                                diaSemana: e.target.value,
                                                            }))
                                                        }
                                                    >
                                                        <option value="">Dia da Semana</option>
                                                        {diasSemanaOptions.map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="flex flex-col items-start gap-2">
                                                    <span className="text-sm">Ocorrência no mês</span>
                                                    <select
                                                        className="select select-sm select-bordered w-64 max-w-full"
                                                        value={recorrencia.ocorrenciaMensal || ''}
                                                        onChange={(e) =>
                                                            setRecorrencia((prev) => ({
                                                                ...prev,
                                                                modoMensal: 'diaSemana',
                                                                ocorrenciaMensal: e.target.value as OcorrenciaMensal,
                                                            }))
                                                        }
                                                    >
                                                        {ocorrenciasMensaisOptions.map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {getOcorrenciaMensalLabel(
                                                                    option.value,
                                                                    recorrencia.diaSemana,
                                                                )}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid w-full gap-2 sm:grid-cols-2">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-sm">Início</span>
                                                <input
                                                    type="date"
                                                    className="input input-sm input-bordered w-full"
                                                    value={recorrencia?.inicio ? moment(recorrencia?.inicio).local().format('YYYY-MM-DD') : ''}
                                                    onChange={(e) => handleInicioRecorrenciaChange(e.target.value)}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <span className="text-sm">Fim</span>
                                                <input
                                                    type="date"
                                                    className="input input-sm input-bordered w-full"
                                                    value={recorrencia?.fim ? moment(recorrencia?.fim).local().format('YYYY-MM-DD') : ''}
                                                    onChange={(e) => {
                                                        setRecorrenciaFimTouched(true);
                                                        setRecorrencia((prev) => ({
                                                            ...prev,
                                                            fim: e.target.value ? moment(e.target.value).local().toDate() : null,
                                                        }));
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {periodoValido ? (
                                            <p className="text-sm text-base-content/70" aria-live="polite">
                                                <span className="font-semibold text-base-content">{describeRegra(recorrencia)}</span>
                                                {`, cerca de ${ocorrenciasEstimadas} ${ocorrenciasEstimadas === 1 ? 'atendimento' : 'atendimentos'} até ${moment(recorrencia.fim).format('DD/MM/YYYY')}.`}
                                            </p>
                                        ) : (
                                            <p className="text-sm text-error" role="alert">
                                                Informe uma data final posterior à data inicial.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                    </div>
                )
            )}
        </div>
    );
};
